import React from "react";
import { motion } from "framer-motion";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { ArrowUpRight, Clock } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import { useServices } from "@/templates/shared/useSiteData";

const Services = () => {
  // siteCopy = dictionary + Site Content overrides + the tenant record (lib/useSiteCopy.js).
  // eyebrow/title/lead/cta stay dictionary-driven; only the individual service cards
  // (BUG-040) come from the tenant's real booking catalogue when it has one.
  const siteCopy = useSiteCopy();
  const services = siteCopy.services;
  const items = useServices();
  const handMassage = siteCopy.images.handMassage;
  // A failed load returns []; the labelled sample catalogue (genuinely-zero) always has
  // entries, so this only hides the section on a load failure — same gate the themed
  // templates use for testimonials/services/blog (`X.length > 0`), never on a real empty
  // catalogue.
  if (items.length === 0) return null;
  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-36 bg-eh-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <span className="eyebrow">{services.eyebrow}</span>
            <h2 className="font-display text-5xl md:text-6xl text-eh-ink mt-4 leading-[1.05] whitespace-pre-line">
              {services.title}
            </h2>
          </div>
          <p className="col-span-12 md:col-span-5 text-lg md:text-xl text-eh-muted leading-relaxed">
            {services.lead}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {items.map((s, i) => {
            const isFeatured = i === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`service-card-${i}`}
                className={`relative col-span-12 md:col-span-6 lg:col-span-6 ${
                  isFeatured ? "lg:col-span-6 lg:row-span-2" : ""
                }`}
              >
                <div
                  className={`group relative h-full rounded-[2rem] overflow-hidden border border-eh-border-soft transition-all duration-500 hover:shadow-[0_24px_60px_-25px_rgba(44,53,49,0.25)] hover:-translate-y-1 ${
                    isFeatured ? "bg-eh-ink text-eh-bg" : "bg-eh-cream text-eh-ink"
                  }`}
                >
                  {isFeatured && (
                    <div className="absolute inset-0 opacity-25 mix-blend-luminosity">
                      <img src={handMassage} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="relative p-9 md:p-11 flex flex-col h-full min-h-[280px]">
                    <div className="flex items-center gap-2 mb-6">
                      <span
                        className={`text-xs uppercase tracking-[0.2em] ${
                          isFeatured ? "text-eh-ochre" : "text-eh-sage-deep"
                        }`}
                      >
                        {s.tag}
                      </span>
                      <span className={`flex items-center gap-1 text-xs ${isFeatured ? "text-eh-bg/70" : "text-eh-muted"}`}>
                        <Clock size={12} /> {s.duration}
                      </span>
                    </div>
                    <h3 className={`font-display text-3xl md:text-4xl leading-tight ${isFeatured ? "text-eh-bg" : "text-eh-ink"}`}>
                      {s.title}
                    </h3>
                    <p className={`mt-4 text-base md:text-lg leading-relaxed max-w-md ${
                      isFeatured ? "text-eh-bg/80" : "text-eh-muted"
                    }`}>
                      {s.body}
                    </p>
                    <div className="mt-auto pt-8">
                      <a
                        href={tenantHref("/reserver")}
                        className={`inline-flex items-center gap-2 text-sm font-medium ${
                          isFeatured ? "text-eh-ochre" : "text-eh-ink"
                        } group-hover:gap-3 transition-all`}
                      >
                        {services.cta}
                        <ArrowUpRight size={16} />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
